import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

// CountUp component'i
const CountUp = ({ end, duration = 2, suffix = '' }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const countRef = useRef(null);
    const isInView = useInView(countRef, { once: true, amount: 0.5 });

    useEffect(() => {
        if (isInView && !isVisible) {
            setIsVisible(true);
        }
    }, [isInView, isVisible]);

    useEffect(() => {
        if (!isVisible) return;

        let startTime = null;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1);

            const easeOut = 1 - Math.pow(1 - progress, 4);
            const currentCount = Math.round(easeOut * end);

            setCount(currentCount);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        const timeout = setTimeout(() => {
            animationFrame = requestAnimationFrame(animate);
        }, 200);

        return () => {
            clearTimeout(timeout);
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [isVisible, end, duration]);

    return (
        <span ref={countRef} className="text-[#3032D4] font-extrabold">
            {count}{suffix}
        </span>
    );
};

const Stats = () => {
    const { language } = useLanguage();
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
    const swiperRef = useRef(null);

    const stats = [
        {
            number: 10,
            suffix: language === 'fa' ? '+ سال' : '+ Years',
            title: language === 'fa' ? 'فعالیت در این عرصه' : 'Experience in this Field',
        },
        {
            number: 60,
            suffix: language === 'fa' ? '+ پروژه' : '+ Projects',
            title: language === 'fa' ? 'اجرای پروژه خاص' : 'Special Project Execution',
        },
        {
            number: 300,
            suffix: '+',
            title: language === 'fa' ? 'مسئولیت برگزاری رویداد' : 'Event Management',
        },
    ];

    return (
        <motion.div
            ref={sectionRef}
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="px-3 sm:px-4 md:px-6 mt-8 sm:mt-12 lg:mt-16 mb-8 sm:mb-12 lg:mb-16"
        >
            <div className="max-w-[1136px] mx-auto relative">
                <div className="relative px-8 sm:px-12 lg:px-16">
                    <Swiper
                        ref={swiperRef}
                        modules={[Navigation]}
                        slidesPerView={3}
                        spaceBetween={30}
                        loop={true}
                        navigation={{
                            prevEl: '.stats-swiper-prev',
                            nextEl: '.stats-swiper-next',
                        }}
                        breakpoints={{
                            320: {
                                slidesPerView: 3,
                                spaceBetween: 4,
                            },
                            480: {
                                slidesPerView: 3,
                                spaceBetween: 6,
                            },
                            640: {
                                slidesPerView: 3,
                                spaceBetween: 10,
                            },
                            768: {
                                slidesPerView: 3,
                                spaceBetween: 16,
                            },
                            1024: {
                                slidesPerView: 3,
                                spaceBetween: 30,
                            },
                        }}
                        className="statsSwiper"
                    >
                        {stats.map((stat, index) => (
                            <SwiperSlide key={index}>
                                <div className="flex flex-col items-center justify-center text-center px-1 sm:px-2 py-2 sm:py-4 lg:py-6 h-full">
                                    <div className="text-[20px] sm:text-[36px] lg:text-[54px] font-extrabold leading-none mb-2 sm:mb-3 lg:mb-4">
                                        <CountUp
                                            end={stat.number}
                                            duration={2.5}
                                            suffix={stat.suffix}
                                        />
                                    </div>

                                    <div className="bg-[#3032D4] rounded-lg px-1 py-1 w-full max-w-[120px] sm:max-w-[180px] lg:max-w-[300px] whitespace-nowrap overflow-hidden">
                                        <p className="text-white text-[10px] sm:text-sm lg:text-lg font-bold truncate">
                                            {stat.title}
                                        </p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                <button
                    onClick={() => swiperRef.current?.swiper?.slidePrev()}
                    className="absolute right-0 md:right-auto md:-left-16 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-8 sm:h-8 lg:w-auto lg:h-auto flex items-center justify-center hover:scale-110 transition-transform"
                >
                    <img src="/union.png" alt="Previous" className="w-full h-full object-contain rotate-180 md:rotate-0" />
                </button>
                <button
                    onClick={() => swiperRef.current?.swiper?.slideNext()}
                    className="absolute left-0 md:left-auto md:-right-16 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-8 sm:h-8 lg:w-auto lg:h-auto flex items-center justify-center hover:scale-110 transition-transform"
                >
                    <img src="/union.png" alt="Next" className="w-full h-full object-contain md:rotate-180 rotate-0" />
                </button>
            </div>
        </motion.div>
    );
};

export default Stats;